import { useState } from "react";
import FadeIn from "../../../../../../src/vendored/FadeIn";
import { Constants } from "../../../../../../common/constants";
import {
	getTiersWithoutPrices,
	PaymentInterval,
	PaymentIntervalToActiveName,
	PriceDescriptor,
	StripePrices,
	StripeProductId,
} from "../../../../../../common/premium_tiers";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { SeshAddonInput } from "../base_components/sesh_addon_input";
import { PricingTier } from "./pricing_tier";
import { WithTooltip } from "../tooltips/with_tooltip";

export function PricingTable(props: {
	link_type: "anchor" | "Link";
	current_pricing: RouterInferenceHelpers["premium"]["getAllProductsPrices"]["output"];
	has_used_free_trial?: boolean;
	onContactClick?: () => void;
}) {
	const [selected_span, setSpan] = useState<PaymentInterval>(
		PaymentInterval.Month
	);

	const { current_pricing } = props;

	const tiers: JSX.Element[] = [];

	const tier_descriptors = getTiersWithoutPrices();

	for (const product_id in tier_descriptors) {
		const casted_product_id = product_id as StripeProductId;
		const current_product = tier_descriptors[casted_product_id];

		for (const current_span in current_product.prices) {
			const span_enum = Number.parseInt(current_span);

			if (PaymentInterval[span_enum] !== PaymentInterval[selected_span]) {
				continue;
			}

			tiers.push(
				<PricingTier
					key={product_id}
					type="regular"
					has_used_free_trial={props.has_used_free_trial}
					product_id={casted_product_id}
					span={span_enum}
					link_type={props.link_type}
					price={current_pricing?.prices[product_id][span_enum]}
					active_coupon={current_pricing?.active_coupon}
					title={current_product.title}
					features={[
						span_enum === PaymentInterval.OneTime ? (
							<>
								<WithTooltip
									style="Info"
									tooltip={
										<div className="max-w-[15rem] whitespace-normal">
											By purchasing a one-time payment
											membership, you will receive premium
											benefits for the lifetime of sesh.
											We intend to continue running sesh
											indefinitely, but there could be a
											(very unlikely) point at which we no
											longer can due to issues with third
											party services (e.g. Discord, Google
											Calendar, Stripe, etc) or other
											unforeseen circumstances.
										</div>
									}
								>
									<span className="border-b border-dotted border-current">
										Lifetime
									</span>
								</WithTooltip>{" "}
								premium for {current_product.activations} server
								{current_product.activations > 1 ? "s" : ""}
							</>
						) : (
							`Premium for ${current_product.activations} server${
								current_product.activations > 1 ? "s" : ""
							}`
						),
						...current_product.features,
					]}
					is_best_deal={current_product.is_best_deal}
					tag_type={
						current_product.is_best_deal ? "Best deal" : undefined
					}
				/>
			);
		}
	}

	// Custom tier removed - now handled separately

	return (
		<>
			<div className="flex flex-col items-center gap-4">
				{/* <p className="note text-center text-sm font-bold !text-sesh-primary sm:text-base">
					Premium sale is going on now, 20% off until February 9th!
				</p> */}
				<SeshAddonInput
					value={selected_span}
					setValue={setSpan}
					buttons={[
						{
							label: PaymentIntervalToActiveName[
								PaymentInterval.Month
							],
							value: PaymentInterval.Month,
						},
						{
							label: (
								<div className="flex items-center gap-1">
									<span>
										{
											PaymentIntervalToActiveName[
												PaymentInterval.Year
											]
										}
									</span>
									<span
										className={`text-[10px] ${
											selected_span ===
											PaymentInterval.Year
												? "text-slate-600"
												: "text-green-400"
										}`}
									>
										(save 30%)
									</span>
								</div>
							),
							value: PaymentInterval.Year,
						},
						{
							label: PaymentIntervalToActiveName[
								PaymentInterval.OneTime
							],
							value: PaymentInterval.OneTime,
						},
					]}
				/>
				<div className="mb-2 text-center">
					<div className="text-sm italic text-slate-200">
						Premium purchases through Stripe can be transferred
						freely between servers
					</div>
				</div>
				<div className="flex w-full flex-wrap justify-center gap-2">
					{tiers}
				</div>
				<div className="mt-4 flex w-full justify-center">
					<div className="w-full max-w-[800px] rounded-lg border border-slate-700 bg-slate-800/30 p-4 text-center">
						<div className="flex items-center justify-center gap-2">
							<i className="fas fa-robot text-lg text-sesh-primary/70"></i>
							<div className="text-sm">
								Looking for custom branding or a completely
								custom bot?{" "}
								<a
									href="#custom-bot"
									className="text-sesh-primary hover:underline"
								>
									See details
								</a>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}

export function centsToDollars(cents: number) {
	return (cents / 100).toFixed(2);
}
